import { signal } from '@preact/signals';
import { gtsArea, gtsCellsInBox } from '../data/gts.ts';
import { fetchCloudCover } from '../data/openmeteo.ts';
import { buildRealConditions, type RealDataSummary } from '../data/realConditions.ts';
import { fetchVarsom, type VarsomForecast } from '../data/varsom.ts';
import { utm33ToWgs84 } from '../terrain/projections.ts';
import { conditions, terrain, viewHour } from './store.ts';

/**
 * "Load real conditions": seNorge snow and weather (NVE), cloud cover (Open-Meteo) and the
 * Varsom forecast for the loaded area, turned into a scenario (data/realConditions.ts).
 *
 * The timeline starts `historyDays` before the chosen time at 06:00 UTC (when seNorge's daily
 * snow depth is stamped) and runs up to two days past it — into the forecast when the chosen
 * time is now — so the scrubber can look ahead.
 */

const HOUR = 3_600_000;
const FORECAST_HOURS = 48;

export type RealDataStatus =
  | { kind: 'idle' }
  | { kind: 'loading'; step: string }
  | { kind: 'done'; summary: RealDataSummary; label: string }
  | { kind: 'error'; message: string };

/** Local calendar date "YYYY-MM-DD". */
function todayLocal(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export const realDataDate = signal<string>(todayLocal());
export const realDataHistoryDays = signal<number>(5);
export const realDataStatus = signal<RealDataStatus>({ kind: 'idle' });
export const varsomForecast = signal<VarsomForecast | null>(null);

let controller: AbortController | null = null;

/**
 * The moment to evaluate at: now (to the hour) for today, otherwise noon UTC on that day; and
 * the timeline's start.
 */
export function realDataWindow(
  date: string,
  historyDays: number,
  now = new Date(),
): { start: Date; target: Date } {
  const isToday = date === todayLocal();
  const target = isToday
    ? new Date(Math.floor(now.getTime() / HOUR) * HOUR)
    : new Date(`${date}T12:00:00Z`);
  const startDay = new Date(target.getTime() - historyDays * 24 * HOUR);
  const start = new Date(
    Date.UTC(startDay.getUTCFullYear(), startDay.getUTCMonth(), startDay.getUTCDate(), 6),
  );
  return { start, target };
}

export async function loadRealConditions(): Promise<void> {
  const t = terrain.peek();
  if (!t) return;
  controller?.abort();
  const ac = new AbortController();
  controller = ac;
  const { signal: abort } = ac;
  const set = (s: RealDataStatus) => {
    if (controller === ac) realDataStatus.value = s;
  };

  try {
    const spec = t.plan.terrain;
    const maxX = spec.minX + spec.cols * spec.cellSize;
    const minY = spec.maxY - spec.rows * spec.cellSize;
    const cells = gtsCellsInBox(spec.minX, minY, maxX, spec.maxY);
    const [lon, lat] = utm33ToWgs84((spec.minX + maxX) / 2, (minY + spec.maxY) / 2);
    const date = realDataDate.peek();
    const { start, target } = realDataWindow(date, realDataHistoryDays.peek());
    const evaluationHour = Math.round((target.getTime() - start.getTime()) / HOUR);
    const end = new Date(target.getTime() + FORECAST_HOURS * HOUR);
    const hours = Math.round((end.getTime() - start.getTime()) / HOUR);

    set({ kind: 'loading', step: 'Fetching seNorge snow and weather…' });
    const hourly = (theme: string) => gtsArea(theme, cells, start, end, true, abort);
    const [snowDepth, swe, temperature, precipitation, windSpeed, windDirection] =
      await Promise.all([
        gtsArea('sd', cells, start, start, false, abort),
        gtsArea('swe', cells, start, start, false, abort),
        hourly('tm1h'),
        hourly('rr1h'),
        hourly('windSpeed10m1h'),
        hourly('windDirection10m1h'),
      ]);
    set({ kind: 'loading', step: 'Fetching cloud cover and the Varsom forecast…' });
    // Both optional: the scenario still works without them.
    const [cloudCover, varsom] = await Promise.all([
      fetchCloudCover(lat, lon, start, hours, abort).catch(() => null),
      fetchVarsom(lat, lon, date, abort).catch(() => null),
    ]);
    if (abort.aborted) return;

    const built = buildRealConditions({
      start,
      evaluationHour,
      minElevation: Math.round(t.minElevation),
      maxElevation: Math.round(t.maxElevation),
      snowDepth,
      swe,
      temperature,
      precipitation,
      windSpeed,
      windDirection,
      cloudCover,
      varsom,
    });
    if (built.conditions.events.length === 0) {
      set({ kind: 'error', message: 'seNorge returned no weather for this period.' });
      return;
    }
    varsomForecast.value = varsom;
    viewHour.value = null;
    conditions.value = { ...built.conditions, startTime: start.toISOString() };
    set({
      kind: 'done',
      summary: built.summary,
      label: `${date}, from ${start.toISOString().slice(0, 13).replace('T', ' ')}:00 UTC`,
    });
  } catch (e) {
    if (abort.aborted) return;
    set({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
  }
}

/**
 * Real time of timeline hour 0 when the scenario comes from observed weather (it's stored in the
 * conditions, so it survives reloads and shared links, and a preset clears it), else null.
 */
export function realDataStart(): Date | null {
  const t = conditions.value.startTime;
  return t ? new Date(t) : null;
}
